import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import ListingTabs from "../../components/Listing/ListingTabs"
import FilterDropdown from "../../components/Listing/FilterDropdown"
import InfiniteListSentinel from "../../components/Listing/InfiniteListSentinel"
import useInfinitePagination from "../../hooks/useInfinitePagination"
import useCallResponseRealtime from "../../hooks/useCallResponseRealtime"
import useReverseListScroll from "../../hooks/useReverseListScroll"
import { Avatar } from "../../components/DashboardComponents/AvatarGroup"
import EmptyState from "../../components/Common/EmptyState"
import MediaFallbackImage from "../../components/Common/MediaFallbackImage"
import DeleteConfirmation from "../../components/Common/DeleteConfirmation"
import CustomButton from "../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../components/OnboardingComponents/CustomIconButton"
import { ICONS } from "../../constants/icons"
import { del, get, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName, nameWithYou } from "../../helpers/userDisplay"
import { setUser } from "../../store/slices/authSlice"
import {
  CALL_DETAIL_WITH,
  CALL_RESPONSE_PAGE_SIZE,
  CALL_RESPONSE_SORT_OPTIONS,
  CITY_CALL_RESPONSE_WITH,
  DEFAULT_CALL_RESPONSE_SORT_BY,
  DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
  buildCallResponseThreads,
  compareCallResponseThreads,
  countCallResponseItems,
  extractCallResponsePage,
  formatPersonName,
  getCallTypeBadgeLabel,
  isCallResponseReverseInfinite,
  mapApiCallToCityHubCard,
  mergeCallResponseItems,
  patchCallResponseLike,
  removeCallResponseById,
  removeCallResponseTree,
  upsertCallResponseItem,
} from "../CallsForArtist/callMappers"
import {
  VISIBLE_CITY_HUB_TABS,
  cityHubTabPath,
  getCityHubTabByPath,
} from "./cityHubTabs"
import {
  findCityHubCityById,
  getCityHubEngageRestriction,
  getUserCityHubLevel,
  mapCallToCityHubCity,
  mapUserAddressesToExploreCities,
  pickDefaultCityHubCity,
} from "./cityHubCity"
import { CityHubAside, CityHubBanner, CityHubGenome } from "./CityHubPanels"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const roleToneClass = (badge = "") => {
  const key = String(badge).toLowerCase()
  if (key.includes("freelance")) return "city-hub-call__role--muted"
  if (key.includes("3d")) return "city-hub-call__role--warm"
  if (key.includes("author")) return "city-hub-call__role--author"
  return "city-hub-call__role--warm"
}

const enrichThreadUser = (item, currentUser) => {
  const isMine = Boolean(
    currentUser?.id && item.userId && String(item.userId) === String(currentUser.id)
  )
  const missingName = !item.name || item.name === "N/A"
  const resolvedName =
    missingName && isMine ? formatPersonName(currentUser) : item.name

  return {
    ...item,
    isMine,
    name: nameWithYou(resolvedName, isMine),
    avatar:
      !item.avatar && isMine ? getUserAvatarUrl(currentUser) : item.avatar,
    comments: (item.comments || []).map(child =>
      enrichThreadUser(child, currentUser)
    ),
  }
}

const CallHeroSkeleton = () => (
  <article
    className="city-hub-call__hero city-hub-call__hero--skeleton"
    aria-busy="true"
    aria-hidden="true"
  >
    <div className="amok-skeleton city-hub-call__skeleton-poster" />
    <div className="city-hub-call__skeleton-body">
      <div className="city-hub-call__skeleton-title-row">
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--title" />
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--badge" />
      </div>
      <div className="amok-skeleton city-hub-call__skeleton-line" />
      <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--md" />
      <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--sm" />
      <div className="city-hub-call__skeleton-entities">
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--entity" />
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--entity" />
      </div>
    </div>
  </article>
)

const CallResponseSkeletons = ({ count = 3 }) =>
  Array.from({ length: count }).map((_, index) => (
    <div
      key={`city-call-response-skeleton-${index}`}
      className="city-hub-call__thread city-hub-call__thread--skeleton"
      aria-hidden="true"
    >
      <div className="amok-skeleton city-hub-call__skeleton-avatar" />
      <div className="city-hub-call__skeleton-thread-body">
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--sm" />
        <div className="amok-skeleton city-hub-call__skeleton-line" />
        <div className="amok-skeleton city-hub-call__skeleton-line city-hub-call__skeleton-line--md" />
      </div>
    </div>
  ))

const hasRoleBadge = badge => {
  const value = String(badge || "").trim()
  return Boolean(value && value !== "N/A")
}

const roleBadge = badge =>
  hasRoleBadge(badge) ? (
    <span className={`city-hub-call__role ${roleToneClass(badge)}`}>
      {badge}
    </span>
  ) : null

const ResponseActions = ({
  item,
  likingId,
  canDelete,
  canEngage = true,
  engageHint,
  onLike,
  onReply,
  onDelete,
}) => (
  <div className="city-hub-call__actions-row">
    <button
      type="button"
      className={`city-hub-call__action city-hub-call__action--like${
        item.liked ? " city-hub-call__action--liked" : ""
      }`}
      aria-pressed={item.liked}
      aria-label={item.liked ? "Unlike response" : "Like response"}
      title={canEngage ? undefined : engageHint}
      disabled={!canEngage || likingId === item.id}
      onClick={() => {
        if (!canEngage) return
        onLike(item)
      }}
    >
      <img
        src={item.liked ? ICONS.heartFill : ICONS.heartOutlineRed}
        alt=""
        width={14}
        height={14}
      />
      <span>{item.likes || 0}</span>
    </button>
    <span className="city-hub-call__action city-hub-call__action--comments">
      <img src={ICONS.messageText} alt="" width={14} height={14} />
      <span>{item.replies || 0}</span>
    </span>
    <button
      type="button"
      className="city-hub-call__reply-link"
      title={canEngage ? undefined : engageHint}
      disabled={!canEngage}
      onClick={() => {
        if (!canEngage) return
        onReply(item)
      }}
    >
      Reply
    </button>
    {canDelete ? (
      <CustomIconButton
        iconSrc={ICONS.trash}
        label="Delete comment"
        tone="danger"
        size="sm"
        className="city-hub-call__action city-hub-call__action--delete"
        onClick={() => onDelete(item)}
      />
    ) : null}
  </div>
)

const CallResponseItem = ({
  item,
  nested = false,
  likingId,
  openReplyId,
  replyText,
  isSubmitting,
  canDelete,
  canEngage = true,
  engageHint,
  onLike,
  onReply,
  onDelete,
  onReplyTextChange,
  onSubmitReply,
}) => {
  const comments = item.comments || []
  const isReplying = canEngage && openReplyId === item.id
  const inlineReply = isReplying ? (
    <div className="city-hub-call__inline-reply">
      <input
        type="text"
        value={replyText}
        onChange={event => onReplyTextChange(event.target.value)}
        onKeyDown={event => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            onSubmitReply()
          }
        }}
        placeholder="Write a reply..."
        aria-label={`Reply to ${item.name || "response"}`}
        disabled={isSubmitting}
      />
      <CustomButton
        variant="primary"
        size="sm"
        type="button"
        disabled={isSubmitting || !String(replyText || "").trim()}
        onClick={onSubmitReply}
      >
        Reply
      </CustomButton>
    </div>
  ) : null

  const deleteBtn = canDelete ? (
    <CustomIconButton
      iconSrc={ICONS.trash}
      label="Delete comment"
      tone="danger"
      size="sm"
      className="city-hub-call__action city-hub-call__action--delete"
      onClick={() => onDelete(item)}
    />
  ) : null

  if (nested) {
    return (
      <div className="city-hub-call__reply-card">
        <div className="city-hub-call__reply-head">
          <Avatar
            src={item.avatar}
            name={item.name}
            size="xs"
            className="city-hub-call__reply-avatar"
          />
          <div className="city-hub-call__reply-meta">
            <div className="city-hub-call__reply-names">
              <span>{item.name || "N/A"}</span>
              {roleBadge(item.badge)}
              {item.author ? (
                <span className="city-hub-call__author">Author</span>
              ) : null}
            </div>
            <span className="city-hub-call__thread-time">
              {item.time || "N/A"}
            </span>
          </div>
          {deleteBtn}
        </div>
        <p className="city-hub-call__reply-text">{item.text || "N/A"}</p>
      </div>
    )
  }

  return (
    <article className="city-hub-call__thread">
      <Avatar
        src={item.avatar}
        name={item.name}
        size="md"
        className="city-hub-call__thread-avatar"
      />
      <div className="city-hub-call__thread-body">
        <div className="city-hub-call__thread-meta">
          <span className="city-hub-call__thread-name">
            {item.name || "N/A"}
          </span>
          {roleBadge(item.badge)}
          <span className="city-hub-call__thread-time">{item.time || "N/A"}</span>
        </div>
        <p className="city-hub-call__thread-text">{item.text || "N/A"}</p>
        <ResponseActions
          item={item}
          likingId={likingId}
          canDelete={canDelete}
          canEngage={canEngage}
          engageHint={engageHint}
          onLike={onLike}
          onReply={onReply}
          onDelete={onDelete}
        />
        {comments.length > 0 ? (
          <div className="city-hub-call__replies">
            {comments.map(comment => (
              <CallResponseItem
                key={comment.id}
                item={comment}
                nested
                canDelete={Boolean(comment.isMine)}
                onDelete={onDelete}
              />
            ))}
          </div>
        ) : null}
        {inlineReply}
      </div>
    </article>
  )
}

/**
 * Call detail — Figma: tabs only (no listing toolbar), hero card + responses.
 * Loads `GET /api/v1/city/:cityId/calls/:callId`.
 */
const CityHubDetail = () => {
  const { callId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.auth.currentUser)

  const [selectedCity, setSelectedCity] = useState(
    () => location.state?.city || null,
  )
  const [isCityLoading, setIsCityLoading] = useState(true)
  const [call, setCall] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rawResponses, setRawResponses] = useState([])
  const [isResponsesLoading, setIsResponsesLoading] = useState(false)
  const [isLoadingMoreResponses, setIsLoadingMoreResponses] = useState(false)
  const [responsePage, setResponsePage] = useState(1)
  const [hasMoreResponses, setHasMoreResponses] = useState(false)
  const [responseTotal, setResponseTotal] = useState(0)
  const [sortBy, setSortBy] = useState(DEFAULT_CALL_RESPONSE_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_CALL_RESPONSE_SORT_DIRECTION
  )
  const [replyText, setReplyText] = useState("")
  const [composerText, setComposerText] = useState("")
  const [replyingTo, setReplyingTo] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [likingId, setLikingId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const composerInputRef = useRef(null)
  const threadsRef = useRef(null)
  const rawResponsesRef = useRef([])
  const markPrependRef = useRef(() => {})
  const currentUserId =
    currentUser?.id != null ? String(currentUser.id) : ""

  rawResponsesRef.current = rawResponses

  document.title = "City Hub Call | AMOK . EARTH"

  const yourCities = useMemo(
    () => mapUserAddressesToExploreCities(currentUser),
    [currentUser]
  )

  const userLevel = useMemo(
    () => getUserCityHubLevel(currentUser),
    [currentUser],
  )

  const navCityId = location.state?.cityId || null
  const navCity = location.state?.city || null

  const resolveLayoutCity = useCallback(
    (cities = yourCities) => {
      if (!navCityId) return null

      const owned = findCityHubCityById(navCityId, cities)
      if (owned) return owned

      if (navCity?.id && String(navCity.id) === String(navCityId)) {
        return navCity
      }

      return null
    },
    [navCity, navCityId, yourCities],
  )

  const city = useMemo(() => {
    const targetId = navCityId

    if (selectedCity?.id) {
      if (!targetId || String(selectedCity.id) === String(targetId)) {
        return selectedCity
      }
    }

    const fromContext = resolveLayoutCity()
    if (fromContext?.id) return fromContext

    if (targetId) return selectedCity

    if (selectedCity?.id) return selectedCity
    return pickDefaultCityHubCity(currentUser)
  }, [currentUser, navCityId, resolveLayoutCity, selectedCity])

  const cityId = navCityId || city?.id || null

  const engageRestriction = useMemo(
    () => getCityHubEngageRestriction(cityId, yourCities, userLevel),
    [cityId, yourCities, userLevel],
  )
  const canEngage = Boolean(engageRestriction.canEngage)

  useEffect(() => {
    if (canEngage) return
    setReplyingTo(null)
    setReplyText("")
    setComposerText("")
  }, [canEngage])

  useEffect(() => {
    const resolved = resolveLayoutCity()
    if (resolved?.id) {
      setSelectedCity(resolved)
    }
  }, [callId, navCityId, navCity, yourCities, resolveLayoutCity])

  useEffect(() => {
    let cancelled = false

    const loadUserCity = async () => {
      setIsCityLoading(true)
      try {
        const response = await get(endpoints.getMe, {
          params: { with: "addresses.city" },
        })
        if (!cancelled && response?.data?.success) {
          dispatch(setUser(response.data.result))
          setSelectedCity(prev => {
            if (prev?.id) return prev

            const cities = mapUserAddressesToExploreCities(response.data.result)
            if (navCityId) {
              const owned = findCityHubCityById(navCityId, cities)
              if (owned) return owned
              if (navCity?.id) return navCity
              return prev
            }

            return pickDefaultCityHubCity(response.data.result)
          })
        }
      } finally {
        if (!cancelled) setIsCityLoading(false)
      }
    }

    loadUserCity()
    return () => {
      cancelled = true
    }
  }, [dispatch, navCity, navCityId])

  const fetchCall = useCallback(async () => {
    if (!callId || !cityId) {
      setCall(null)
      if (!callId) setError("Call not found")
      setIsLoading(Boolean(callId) && !cityId)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await get(
        endpoints.getCityCallById(cityId, callId),
        {
          params: { "with[]": CALL_DETAIL_WITH },
        }
      )

      if (response?.data?.success) {
        const raw = response.data.result || {}
        setCall(mapApiCallToCityHubCard(raw))
        const hubCity = mapCallToCityHubCity(raw, cityId)
        if (hubCity?.id) {
          setSelectedCity(prev => {
            const owned = findCityHubCityById(hubCity.id, yourCities)
            return owned || hubCity
          })
        }
      } else {
        const message = toastApiError(response, "Failed to load city call")
        setError(message)
        setCall(null)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load city call")
      setError(message)
      setCall(null)
    } finally {
      setIsLoading(false)
    }
  }, [callId, cityId, yourCities])

  const fetchResponses = useCallback(
    async ({ silent = false, page: pageToLoad = 1, append = false } = {}) => {
      if (!callId || !cityId) {
        setRawResponses([])
        setHasMoreResponses(false)
        setResponseTotal(0)
        return
      }

      if (append) {
        setIsLoadingMoreResponses(true)
      } else if (!silent) {
        setIsResponsesLoading(true)
      }

      try {
        const responsesRes = await get(
          endpoints.getCityCallResponses(cityId, callId),
          {
            params: {
              page: pageToLoad,
              per_page: CALL_RESPONSE_PAGE_SIZE,
              sort_by: sortBy,
              sort_direction: sortDirection,
              "with[]": CITY_CALL_RESPONSE_WITH,
            },
          }
        )

        if (!responsesRes?.data?.success) {
          if (responsesRes) {
            toastApiError(responsesRes, "Failed to load responses")
          }
          if (!append) {
            setRawResponses([])
            setHasMoreResponses(false)
            setResponseTotal(0)
          }
          return
        }

        const { list, currentPage, lastPage, total } =
          extractCallResponsePage(responsesRes)

        if (
          append &&
          isCallResponseReverseInfinite(sortBy, sortDirection)
        ) {
          markPrependRef.current?.()
        }

        setRawResponses(prev =>
          append ? mergeCallResponseItems(prev, list) : list
        )
        setResponsePage(currentPage)
        setHasMoreResponses(currentPage < lastPage)
        setResponseTotal(Number.isFinite(total) ? Math.max(0, total) : list.length)
      } catch (err) {
        toastApiError(err, "Failed to load responses")
        if (!append) {
          setRawResponses([])
          setHasMoreResponses(false)
          setResponseTotal(0)
        }
      } finally {
        setIsResponsesLoading(false)
        setIsLoadingMoreResponses(false)
      }
    },
    [callId, cityId, sortBy, sortDirection]
  )

  useEffect(() => {
    fetchCall()
  }, [fetchCall])

  useEffect(() => {
    if (!callId || !cityId) return undefined
    fetchResponses({ page: 1, append: false })
    return undefined
  }, [callId, cityId, fetchResponses])

  const responses = useMemo(() => {
    const authorUserId =
      call?.raw?.user_id || call?.raw?.user?.id || call?.postedBy?.id || null
    return buildCallResponseThreads({
      responses: rawResponses,
      likes: [],
      authorUserId,
      callId,
    }).map(item => enrichThreadUser(item, currentUser))
  }, [
    rawResponses,
    call?.raw?.user_id,
    call?.raw?.user?.id,
    call?.postedBy?.id,
    callId,
    currentUser,
  ])

  const sortedResponses = useMemo(
    () =>
      [...responses].sort((a, b) =>
        compareCallResponseThreads(a, b, sortBy, sortDirection)
      ),
    [responses, sortBy, sortDirection]
  )

  const isReverseInfinite = isCallResponseReverseInfinite(
    sortBy,
    sortDirection
  )
  const displayedResponses = isReverseInfinite
    ? [...sortedResponses].reverse()
    : sortedResponses

  const { markPrepend } = useReverseListScroll({
    enabled: isReverseInfinite && !isResponsesLoading,
    containerRef: threadsRef,
    itemsKey: `${callId}-${displayedResponses.length}`,
    isLoading: isResponsesLoading || isLoadingMoreResponses,
    resetKey: `${callId}-${sortBy}-${sortDirection}`,
  })
  markPrependRef.current = markPrepend

  const loadMoreResponses = useCallback(() => {
    if (
      !hasMoreResponses ||
      isResponsesLoading ||
      isLoadingMoreResponses
    ) {
      return
    }
    fetchResponses({
      page: responsePage + 1,
      append: true,
      silent: true,
    })
  }, [
    fetchResponses,
    hasMoreResponses,
    isResponsesLoading,
    isLoadingMoreResponses,
    responsePage,
  ])

  const { sentinelRef } = useInfinitePagination({
    hasMore: hasMoreResponses && !isResponsesLoading,
    isLoading: isLoadingMoreResponses,
    onLoadMore: loadMoreResponses,
    resetKey: `${callId}-${displayedResponses.length}`,
    rootRef: threadsRef,
  })

  const tabItems = useMemo(
    () =>
      VISIBLE_CITY_HUB_TABS.map(tab => ({
        key: tab.path,
        label: tab.label,
        icon: tab.icon,
      })),
    []
  )

  const projectCode = call?.projectCode || "N/A"
  const responseCount = Math.max(
    0,
    Number(responseTotal) || Number(call?.responses) || 0
  )

  const composerUser = {
    name: getUserFullName(currentUser, { fallback: "You" }),
    avatar: getUserAvatarUrl(currentUser),
  }

  const navigateToCityHubTab = useCallback(
    (tabKey, { replace = false } = {}) => {
      const nextTab = getCityHubTabByPath(tabKey) || tabKey
      if (!nextTab || !cityId) return
      navigate(cityHubTabPath(nextTab, cityId), {
        replace,
        state: {
          cityId,
          city: city || undefined,
        },
      })
    },
    [city, cityId, navigate],
  )

  const handleTabChange = key => {
    navigateToCityHubTab(key)
  }

  const handleStartReply = useCallback(
    item => {
      if (!canEngage || !item?.id) return
      setReplyingTo(prev => {
        if (prev?.id === item.id) {
          setReplyText("")
          return null
        }
        setReplyText("")
        return { id: item.id, name: item.name }
      })
    },
    [canEngage],
  )

  const submitComment = useCallback(
    async (body, parentId = null) => {
      const text = String(body || "").trim()
      if (!canEngage || !text || !cityId || !callId || isSubmitting) return

      setIsSubmitting(true)
      try {
        const payload = { body: text }
        if (parentId) payload.parent_comment_id = parentId

        const response = await post(
          endpoints.createCityCallResponse(cityId, callId),
          payload
        )

        if (!response?.data?.success) return

        toast.success(response.data.message || "Response posted", TOAST_OPTIONS)
        if (parentId) {
          setReplyText("")
          setReplyingTo(null)
        } else {
          setComposerText("")
        }
        setCall(prev =>
          prev
            ? {
                ...prev,
                responses: Math.max(0, Number(prev.responses || 0) + 1),
              }
            : prev
        )
        await fetchResponses({ silent: true })
      } catch (err) {
        toastApiError(err, "Failed to post response")
      } finally {
        setIsSubmitting(false)
      }
    },
    [canEngage, cityId, callId, isSubmitting, fetchResponses]
  )

  const handleSubmitReply = useCallback(() => {
    submitComment(replyText, replyingTo?.id || null)
  }, [submitComment, replyText, replyingTo])

  const handleSubmitComposer = useCallback(() => {
    submitComment(composerText)
  }, [submitComment, composerText])

  const handleToggleLike = useCallback(
    async item => {
      if (
        !canEngage ||
        !item?.id ||
        item.parentId ||
        !cityId ||
        !callId ||
        likingId
      )
        return

      const previousLiked = Boolean(item.liked)
      const previousLikes = Number(item.likes || 0)

      setLikingId(item.id)
      setRawResponses(prev =>
        patchCallResponseLike(prev, item.id, {
          liked: !previousLiked,
          likes: Math.max(0, previousLikes + (previousLiked ? -1 : 1)),
        })
      )

      try {
        const response = await post(
          endpoints.toggleCityCallResponseLike(cityId, callId, item.id),
          {}
        )
        if (!response?.data?.success) {
          setRawResponses(prev =>
            patchCallResponseLike(prev, item.id, {
              liked: previousLiked,
              likes: previousLikes,
            })
          )
          toastApiError(response, "Failed to update like")
          return
        }

        const result = response.data.result
        if (result && typeof result === "object") {
          setRawResponses(prev =>
            patchCallResponseLike(prev, item.id, {
              liked: Boolean(result.has_current_user_liked ?? !previousLiked),
              likes: Number.isFinite(Number(result.likes_count))
                ? Math.max(0, Number(result.likes_count))
                : Math.max(0, previousLikes + (previousLiked ? -1 : 1)),
            })
          )
        }
      } catch (err) {
        setRawResponses(prev =>
          patchCallResponseLike(prev, item.id, {
            liked: previousLiked,
            likes: previousLikes,
          })
        )
        toastApiError(err, "Failed to update like")
      } finally {
        setLikingId(null)
      }
    },
    [canEngage, cityId, callId, likingId]
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.id || !cityId || !callId) return

    setIsDeleting(true)
    try {
      const response = await del(
        endpoints.deleteCityCallResponse(cityId, callId, deleteTarget.id)
      )
      if (!response?.data?.success) {
        toastApiError(response, "Failed to delete comment")
        return
      }

      toast.success(response.data.message || "Comment deleted", TOAST_OPTIONS)
      const { next, removedCount } = removeCallResponseTree(
        rawResponses,
        deleteTarget.id
      )
      setRawResponses(next)
      setResponseTotal(prev => Math.max(0, Number(prev || 0) - removedCount))
      setCall(prev =>
        prev
          ? {
              ...prev,
              responses: Math.max(
                0,
                Number(prev.responses || 0) - removedCount
              ),
            }
          : prev
      )
      if (replyingTo?.id && String(replyingTo.id) === String(deleteTarget.id)) {
        setReplyingTo(null)
        setReplyText("")
      }
      setDeleteTarget(null)
    } catch (err) {
        toastApiError(err, "Failed to delete comment")
    } finally {
      setIsDeleting(false)
    }
  }, [deleteTarget, cityId, callId, rawResponses, replyingTo])

  const handleRealtimeCreated = useCallback(response => {
    if (!response?.id && response?.id !== 0) return
    const id = String(response.id)
    const alreadyExists = rawResponsesRef.current.some(
      item => String(item?.id) === id
    )
    setRawResponses(prev => upsertCallResponseItem(prev, response))
    if (alreadyExists) return
    setResponseTotal(prev => Math.max(0, Number(prev || 0) + 1))
    setCall(prev =>
      prev
        ? {
            ...prev,
            responses: Math.max(0, Number(prev.responses || 0) + 1),
          }
        : prev
    )
  }, [])

  const handleRealtimeUpdated = useCallback(response => {
    if (!response?.id && response?.id !== 0) return
    setRawResponses(prev =>
      upsertCallResponseItem(prev, response, { replace: true })
    )
  }, [])

  const handleRealtimeDeleted = useCallback(
    responseId => {
      if (responseId == null || responseId === "") return
      const prev = rawResponsesRef.current
      const before = countCallResponseItems(prev)
      const next = removeCallResponseById(prev, responseId)
      const removedCount = before - countCallResponseItems(next)
      if (removedCount <= 0) return

      setRawResponses(next)
      setResponseTotal(total => Math.max(0, Number(total || 0) - removedCount))
      setCall(callPrev =>
        callPrev
          ? {
              ...callPrev,
              responses: Math.max(
                0,
                Number(callPrev.responses || 0) - removedCount
              ),
            }
          : callPrev
      )
      if (replyingTo?.id && String(replyingTo.id) === String(responseId)) {
        setReplyingTo(null)
        setReplyText("")
      }
    },
    [replyingTo]
  )

  const handleRealtimeLike = useCallback(
    ({ commentId, likesCount, userId, removed = false } = {}) => {
      if (commentId == null || commentId === "") return
      const isCurrentUser =
        currentUserId && userId && String(userId) === currentUserId
      setRawResponses(prev => {
        const current = prev.find(
          item => String(item?.id) === String(commentId)
        )
        const nextLikes =
          likesCount != null
            ? likesCount
            : Math.max(
                0,
                Number(current?.likes_count || 0) + (removed ? -1 : 1)
              )
        return patchCallResponseLike(prev, commentId, {
          likes: nextLikes,
          liked: isCurrentUser ? !removed : undefined,
        })
      })
    },
    [currentUserId]
  )

  useCallResponseRealtime({
    callId,
    enabled: Boolean(callId),
    onCreated: handleRealtimeCreated,
    onUpdated: handleRealtimeUpdated,
    onDeleted: handleRealtimeDeleted,
    onLike: handleRealtimeLike,
  })

  const canDeleteResponse = useCallback(
    item =>
      Boolean(
        item?.isMine ||
          (currentUserId &&
            item?.userId &&
            String(item.userId) === currentUserId),
      ),
    [currentUserId],
  )

  const showCallSkeleton = isLoading || Boolean(callId && !cityId && isCityLoading)
  const callTypeLabel = getCallTypeBadgeLabel(call?.type)
  const callTypeKey = String(call?.type || "")
    .trim()
    .toLowerCase()
  const posterIsYou = Boolean(
    currentUserId &&
      call?.poster?.id &&
      String(call.poster.id) === currentUserId,
  )
  const posterDisplayName = nameWithYou(
    call?.poster?.name || "N/A",
    posterIsYou,
  )

  return (
    <div className="page-content">
      <div className="city-hub city-hub--detail">
        <CityHubBanner
          city={city}
          yourCities={yourCities}
          isLoading={isCityLoading}
          canExplore={false}
        />
        <CityHubGenome city={city} />

        <div className="city-hub__main">
          <div className="city-hub__tabs-row">
            <ListingTabs
              items={tabItems}
              activeKey="calls-for-artists"
              onChange={handleTabChange}
            />
          </div>

          <div
            className={`city-hub-call${
              !showCallSkeleton && call ? " city-hub-call--live" : ""
            }`}
          >
            {showCallSkeleton ? (
              <>
                <CallHeroSkeleton />
                <div className="city-hub-call__responses">
                  <div className="city-hub-call__threads" aria-busy="true">
                    <CallResponseSkeletons />
                  </div>
                </div>
              </>
            ) : null}

            {!showCallSkeleton && !call ? (
              <EmptyState
                title="Call not found"
                description={
                  error ||
                  (!cityId
                    ? "Select a city to view this call."
                    : "This call could not be loaded.")
                }
                actionText="Back to Calls"
                onActionClick={() =>
                  navigateToCityHubTab("calls-for-artists")
                }
              />
            ) : null}

            {!showCallSkeleton && call ? (
              <>
                <article className="city-hub-call__hero">
                  <MediaFallbackImage
                    src={call.image}
                    fallback={ICONS.thumbnailPlaceholder}
                    alt=""
                    className="city-hub-call__poster"
                  />

                  <div className="city-hub-call__body">
                    <div className="city-hub-call__title-row">
                      <h1 className="city-hub-call__title">
                        {call.headline || "N/A"}
                      </h1>
                      <div className="city-hub-call__status-wrap">
                        {callTypeLabel ? (
                          <span
                            className={`city-hub-call__badge city-hub-call__badge--${callTypeKey}`}
                          >
                            {callTypeLabel}
                          </span>
                        ) : null}
                        <span className="city-hub-call__badge">
                          {call.active ? "Active" : call.status}
                        </span>
                        {call.deadline ? (
                          <span className="city-hub-call__deadline">
                            {call.deadline}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="city-hub-call__bottom">
                    <p className="city-hub-call__desc">{call.description}</p>

                    <div className="city-hub-call__lower">
                      <div className="city-hub-call__copy">
                        <div className="city-hub-call__meta">
                          <span className="city-hub-call__location">
                            <img
                              src={ICONS.callLocationGreen}
                              alt=""
                              width={15}
                              height={15}
                            />
                            {call.city || "Global"}
                          </span>
                          {(call.categoryNames || []).map(name => (
                            <span
                              key={name}
                              className="city-hub-call__category"
                            >
                              {name}
                            </span>
                          ))}
                          <span className="city-hub-call__posted">
                            Posted {call.posted}
                          </span>
                          <span className="city-hub-call__dot" aria-hidden="true">
                            •
                          </span>
                          <span className="city-hub-call__count">
                            {responseCount} responses
                          </span>
                        </div>
                      </div>

                      <div className="city-hub-call__entities">
                        {call.project?.name ? (
                          <div className="city-hub-call__entity">
                            <span className="city-hub-call__entity-code city-hub-call__entity-code--green">
                              {projectCode}
                            </span>
                            <div className="city-hub-call__entity-text">
                              <span>Project:</span>
                              <strong>{call.project.name}</strong>
                            </div>
                          </div>
                        ) : null}
                        <div className="city-hub-call__entity">
                          <Avatar
                            src={call.poster?.avatar}
                            name={call.poster?.name}
                            size="sm"
                            className="city-hub-call__entity-avatar"
                          />
                          <div className="city-hub-call__entity-text">
                            <span>Posted by:</span>
                            <strong>{posterDisplayName}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>
                </article>

                <div className="city-hub-call__responses">
                  <div className="city-hub-call__responses-head">
                    <h2 className="city-hub-call__responses-title">
                      {responseCount} Responses
                    </h2>
                    <FilterDropdown
                      label="Sort by"
                      className="city-hub-call__sort-filter"
                      filters={[
                        {
                          key: "sortBy",
                          type: "sortBy",
                          label: "Sort By",
                          value: sortBy,
                          defaultValue: DEFAULT_CALL_RESPONSE_SORT_BY,
                          onChange: next =>
                            setSortBy(next || DEFAULT_CALL_RESPONSE_SORT_BY),
                          options: CALL_RESPONSE_SORT_OPTIONS,
                        },
                        {
                          key: "sortDirection",
                          type: "sortDirection",
                          label: "Direction",
                          value: sortDirection,
                          defaultValue: DEFAULT_CALL_RESPONSE_SORT_DIRECTION,
                          onChange: next =>
                            setSortDirection(
                              next || DEFAULT_CALL_RESPONSE_SORT_DIRECTION
                            ),
                        },
                      ]}
                    />
                  </div>

                  <div
                    ref={threadsRef}
                    className="city-hub-call__threads"
                    aria-busy={
                      isResponsesLoading || isLoadingMoreResponses || undefined
                    }
                  >
                    {isReverseInfinite ? (
                      <>
                        <InfiniteListSentinel
                          sentinelRef={sentinelRef}
                          hasMore={hasMoreResponses && !isResponsesLoading}
                        />
                        {isLoadingMoreResponses ? (
                          <CallResponseSkeletons count={2} />
                        ) : null}
                      </>
                    ) : null}

                    {isResponsesLoading ? <CallResponseSkeletons /> : null}

                    {!isResponsesLoading &&
                      displayedResponses.map(response => (
                        <CallResponseItem
                          key={response.id}
                          item={response}
                          likingId={likingId}
                          openReplyId={replyingTo?.id}
                          replyText={replyText}
                          isSubmitting={isSubmitting}
                          canDelete={canDeleteResponse(response)}
                          canEngage={canEngage}
                          engageHint={engageRestriction.hint}
                          onLike={handleToggleLike}
                          onReply={handleStartReply}
                          onDelete={setDeleteTarget}
                          onReplyTextChange={setReplyText}
                          onSubmitReply={handleSubmitReply}
                        />
                      ))}

                    {!isReverseInfinite && isLoadingMoreResponses ? (
                      <CallResponseSkeletons count={2} />
                    ) : null}

                    {!isReverseInfinite ? (
                      <InfiniteListSentinel
                        sentinelRef={sentinelRef}
                        hasMore={hasMoreResponses && !isResponsesLoading}
                      />
                    ) : null}

                    {!isResponsesLoading &&
                    !isLoadingMoreResponses &&
                    sortedResponses.length === 0 ? (
                      <EmptyState
                        title="No responses yet"
                        description="When artists respond to this call, they’ll show up here."
                      />
                    ) : null}
                  </div>

                  <div className="city-hub-call__composer-dock">
                    {!canEngage && engageRestriction.context ? (
                      <p className="city-hub-call__composer-context">
                        {engageRestriction.context}
                      </p>
                    ) : null}
                    <div
                      className={`city-hub-call__composer${
                        !canEngage ? " city-hub-call__composer--disabled" : ""
                      }`}
                    >
                      <Avatar
                        src={composerUser.avatar}
                        name={composerUser.name}
                        size="sm"
                        className="city-hub-call__composer-avatar"
                      />
                      <div className="city-hub-call__composer-field">
                        <input
                          ref={composerInputRef}
                          type="text"
                          value={composerText}
                          onChange={e => setComposerText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault()
                              handleSubmitComposer()
                            }
                          }}
                          placeholder={engageRestriction.placeholder}
                          aria-label="Write a response"
                          title={canEngage ? undefined : engageRestriction.hint}
                          disabled={!canEngage || isSubmitting}
                        />
                        <button
                          type="button"
                          className="city-hub-call__composer-send"
                          aria-label="Send response"
                          title={canEngage ? undefined : engageRestriction.hint}
                          disabled={
                            !canEngage || isSubmitting || !composerText.trim()
                          }
                          onClick={handleSubmitComposer}
                        >
                          <img src={ICONS.send} alt="" width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <CityHubAside city={city} />
      </div>

      <DeleteConfirmation
        isOpen={Boolean(deleteTarget)}
        onCancel={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default CityHubDetail
